import { useId, useMemo } from 'react';
import classNames from '@utils/classnames';

import { TrailerType } from 'src/types/enums';
import { Checkbox } from 'components/checkbox/Checkbox';
import { nullthrows } from '../../../../utils/invariant';
import { useTranslation } from '../../../../contexts/translation-context';
import type { TranslationKey } from '../../../../translation/types';

const TRAILER_TYPE_ITEMS: {
  translationKey: TranslationKey;
  value: TrailerType;
  icon: string;
}[] = [
  {
    translationKey: 'trailerTypes.Tautliner',
    value: TrailerType.Tautliner,
    icon: '/static/illustrations/TD-Tautliner-v2.svg',
  },
  {
    translationKey: 'trailerTypes.BoxTrailer',
    value: TrailerType.BoxTrailer,
    icon: '/static/illustrations/TD-Box-v2.svg',
  },
  {
    translationKey: 'trailerTypes.Mega',
    value: TrailerType.Mega,
    icon: '/static/illustrations/TD-Mega-v2.svg',
  },
  {
    translationKey: 'trailerTypes.RoadTrain',
    value: TrailerType.RoadTrain,
    icon: '/static/illustrations/TD-Road-train-v2.svg',
  },
  {
    translationKey: 'trailerTypes.FlatBed',
    value: TrailerType.FlatBed,
    icon: '/static/illustrations/TD-Flat-v2.svg',
  },
  {
    translationKey: 'trailerTypes.Refrigerated',
    value: TrailerType.Refrigerated,
    icon: '/static/illustrations/TD-Refrigerated-v2.svg',
  },
  {
    translationKey: 'trailerTypes.Joloda',
    value: TrailerType.Joloda,
    icon: '/static/illustrations/TD-Tautliner-v2.svg',
  },
  {
    translationKey: 'trailerTypes.TautlinerCoil',
    value: TrailerType.TautlinerCoil,
    icon: '/static/illustrations/TD-Tautliner-v2.svg',
  },
];

export interface ITrailerTypeIconProps {
  type: TrailerType;
  variant?: 'regular' | 'small';
}

export const TrailerTypeIcon: React.FC<ITrailerTypeIconProps> = (props) => {
  const { type, variant = 'regular' } = props;
  const { i18n } = useTranslation();

  const value = nullthrows(
    TRAILER_TYPE_ITEMS.find((v) => v.value === type),
    'Trailer type not found',
  );
  const title = i18n(value.translationKey);

  return (
    <div className="flex flex-col items-center">
      <img
        src={value.icon}
        alt={title}
        className={classNames({
          'w-24 h-24': variant === 'regular',
          'w-12 h-14': variant === 'small',
        })}
      />
      <div
        className={classNames('my-2 text-fg-secondary font-regular', {
          'text-sm': variant === 'regular',
          'text-xs': variant === 'small',
        })}
      >
        {title}
      </div>
    </div>
  );
};

export interface ITrailerTypesProps {
  value: TrailerType[];
  onChange: (newValues: TrailerType[]) => void;
  isDisabled?: boolean;
  isMultiSelect?: boolean;
  isRequired?: boolean;
  variant?: 'regular' | 'small';
}

export const TrailerTypes: React.FC<ITrailerTypesProps> = (props) => {
  const { value, onChange, isDisabled, isMultiSelect, variant = 'regular', isRequired } = props;
  const id = useId();
  const { i18n } = useTranslation();

  const items = useMemo(
    () =>
      TRAILER_TYPE_ITEMS.map((v) => ({
        ...v,
        title: i18n(v.translationKey),
      })),
    [i18n],
  );

  return (
    <div className="grid sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-4 gap-y-8">
      {items.map((v) => {
        const key = id + v.value;
        const isChecked = value.includes(v.value);

        return (
          <label
            id={key}
            key={key}
            className={classNames(
              'flex flex-col items-center cursor-pointer border rounded-md hover:bg-surface-hover p-4 relative select-none',
              {
                'border-border': !isChecked,
                'border-brand': isChecked,
                'p-4': variant === 'regular',
                'p-2': variant === 'small',
              },
            )}
          >
            <div className="flex gap-2 items-center justify-start w-full mb-4">
              <Checkbox
                isChecked={isChecked}
                isDisabled={isDisabled}
                onChange={(newIsChecked) => {
                  if (isDisabled) return;

                  if (newIsChecked) {
                    if (isMultiSelect) {
                      onChange([...new Set([...value, v.value])]);
                    } else {
                      onChange([v.value]);
                    }
                  } else {
                    if (isRequired && value.length <= 1) {
                      return;
                    }

                    onChange(value.filter((item) => item !== v.value));
                  }
                }}
              />

              <div className="font-regular">{v.title}</div>
            </div>

            <img
              src={v.icon}
              alt={v.title}
              className={classNames({
                'w-24 h-24': variant === 'regular',
                'w-12 h-14': variant === 'small',
              })}
            />
          </label>
        );
      })}
    </div>
  );
};
